import React from "react";
import { Link } from "react-router-dom";
import { Network, MapPin, Phone, Mail, ArrowUpRight } from "lucide-react";
import { Wordmark } from "./Wordmark";
import { NETWORK_APPS, CONTACTS, NETWORK_URL, slugify } from "@/lib/networkData";
import { WhatsAppIcon } from "./WhatsAppIcon";

export const Footer = () => (
  <footer data-testid="site-footer" className="relative z-10 border-t border-border bg-secondary/20">
    {/* Prominent Network CTA */}
    <div className="border-b border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground text-center sm:text-left">
          Rexxpons fa parte di un ecosistema di strumenti cognitivi e legali.
        </p>
        <a
          data-testid="network-cta-footer"
          href={NETWORK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-primary-foreground bg-primary hover:opacity-90 transition-opacity px-5 py-2.5 rounded-full shrink-0"
        >
          <Network strokeWidth={1.8} className="w-4 h-4" /> Visita il Network
        </a>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
      {/* Brand */}
      <div className="min-w-0">
        <Wordmark className="text-xl text-foreground" />
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-xs">
          The Cognitive Web Browser — beyond search, into understanding. An original project by Attorney Luigi Galano.
        </p>
      </div>

      {/* Network apps */}
      <div className="min-w-0">
        <h3 className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-primary mb-4">
          <Network strokeWidth={1.6} className="w-4 h-4" /> Le App del Network
        </h3>
        <ul className="space-y-2.5">
          {NETWORK_APPS.map((app) => (
            <li key={app.name}>
              <a
                data-testid={`network-link-${slugify(app.name)}`}
                href={app.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-start gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors break-words"
              >
                <span>{app.name}</span>
                <ArrowUpRight strokeWidth={1.6} className="w-3.5 h-3.5 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Contacts */}
      <div className="min-w-0">
        <h3 className="text-xs tracking-[0.2em] uppercase text-primary mb-4">Contatti</h3>
        <p className="text-base font-semibold text-foreground">{CONTACTS.firm}</p>
        <p className="text-xs tracking-[0.16em] uppercase text-muted-foreground mb-4">Law Firm</p>
        <ul className="space-y-3 text-sm text-muted-foreground">
          <li className="flex items-start gap-2.5">
            <MapPin strokeWidth={1.6} className="w-4 h-4 mt-0.5 text-primary shrink-0" />
            <span>{CONTACTS.address}</span>
          </li>
          <li className="flex items-start gap-2.5">
            <Phone strokeWidth={1.6} className="w-4 h-4 mt-0.5 text-primary shrink-0" />
            <a href={CONTACTS.telHref} className="hover:text-foreground transition-colors">{CONTACTS.telDisplay}</a>
          </li>
          <li className="flex items-start gap-2.5">
            <Mail strokeWidth={1.6} className="w-4 h-4 mt-0.5 text-primary shrink-0" />
            <a href={CONTACTS.emailHref} className="hover:text-foreground transition-colors break-all">
              {CONTACTS.email} <span className="text-[10px] tracking-[0.16em] uppercase text-primary/70">· PEC</span>
            </a>
          </li>
        </ul>

        {/* Quick assistance — large green WhatsApp button, no phone number shown */}
        <div className="mt-6">
          <h4 className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3">Assistenza rapida</h4>
          <a
            data-testid="whatsapp-footer"
            href={CONTACTS.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3 rounded-full shadow-lg shadow-emerald-500/30 transition-colors"
          >
            <WhatsAppIcon className="w-5 h-5" />
            Chat su WhatsApp
          </a>
        </div>
      </div>
    </div>

    <div className="border-t border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Luigi Galano · Rexxpons</p>
        <Link to="/pricing" className="text-xs tracking-[0.14em] uppercase text-primary hover:opacity-80 transition-opacity">Pricing</Link>
      </div>
    </div>
  </footer>
);
